import { type MaybeRef, type Ref, computed, ref, unref } from "vue";
import { useI18n } from "vue-i18n";

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/vue-query";
import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";

import { convertToArray } from "@/helpers/common";
import { useNotification } from "@/hooks/notifications";
import communicationService from "@/services/modules/communication";
import emailService from "@/services/modules/emails";
import notesService from "@/services/modules/notes";
import useEmailsStore from "@/stores/emails";
import useNotesStore from "@/stores/notes";

import type { EmailFormPayload } from "@/components/emailSending/EmailForm.vue";
import type { LogEndpointType } from "@/enums/communicationLogs";
import type { IPaginatedResponse } from "@/models/common";
import type {
  CallLogData,
  CallNotePayload,
  CommunicationFilters,
  CommunicationLogBase,
  CommunicationLogComment,
  CommunicationLogResponse,
  EmailThreadItem,
  TaskLogData,
} from "@/models/communicationLogs";
import type { Note, NotePayload } from "@/models/notes";

const PER_PAGE_STEP = "15";
const THREADS_PER_PAGE_STEP = "30";
export const COMMUNICATION_LOGS_KEY = "communication-logs";
const EMAIL_MODAL_DATA_KEY = "email-modal-data";
const EMAIL_THREAD_KEY = "email-thread";

const QUERY_KEYS = {
  communicationLogs: (
    params: Ref<Partial<CommunicationFilters>>,
    isThreaded?: MaybeRef<boolean>
  ) => [COMMUNICATION_LOGS_KEY, params, isThreaded],
  emailModalData: (application_ids: string[]) => [
    EMAIL_MODAL_DATA_KEY,
    application_ids,
  ],
  emailThread: (id: Ref<number>) => [EMAIL_THREAD_KEY, id],
} as const;

export interface CommentPayload {
  logId: CommunicationLogBase["id"];
  type: LogEndpointType;
  comment: CommunicationLogComment["comment"];
}

export const useCommunicationLogsQuery = (
  params: Ref<Partial<CommunicationFilters>>,
  isThreaded: Ref<boolean> = ref(false)
) => {
  const queryKey = computed(() =>
    QUERY_KEYS.communicationLogs(params, isThreaded)
  );

  const prepareParams = (
    params: MaybeRef<Partial<CommunicationFilters>> = {},
    pageParam: number
  ): Partial<CommunicationFilters> => {
    const preparedParams = cloneDeep(unref(params));

    preparedParams.per_page = isThreaded.value
      ? THREADS_PER_PAGE_STEP
      : PER_PAGE_STEP;

    preparedParams.page = String(pageParam);

    /**
     * TODO: update this logic when we support multiple types.
     * For now we can only pass one type,
     * but this will be updated in the following PRs
     */
    if (preparedParams.type) {
      preparedParams.type = convertToArray(preparedParams.type)[0];
    }

    const arrayParams: Array<keyof CommunicationFilters> = [
      "application_ids",
      "assignee_ids",
      "call_session_caller_ids",
      "note_created_at",
    ];

    arrayParams.forEach((param) => {
      const value = preparedParams[param];

      if (value) {
        const valueArray = Array.isArray(value) ? value : [value];
        Object.assign(preparedParams, { [param]: valueArray });
      }
    });

    if (isThreaded.value) {
      preparedParams.is_thread = "1";
    }

    return preparedParams;
  };

  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: async ({ pageParam = 1 }): Promise<CommunicationLogResponse> => {
      const preparedParams = prepareParams(params, pageParam);
      const response =
        await communicationService.getCommunicationList(preparedParams);
      return response;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
  });
};

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (payload: CommentPayload): Promise<CommunicationLogComment> =>
      communicationService.createComment(
        payload.type,
        payload.logId,
        payload.comment
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useUpdateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (
      payload: CommentPayload & { commentId: CommunicationLogComment["id"] }
    ): Promise<CommunicationLogComment> =>
      communicationService.updateComment(
        payload.type,
        payload.logId,
        payload.commentId,
        payload.comment
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (
      payload: Omit<CommentPayload, "comment"> & {
        commentId: CommunicationLogComment["id"];
      }
    ) =>
      communicationService.deleteComment(
        payload.type,
        payload.logId,
        payload.commentId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (
      payload: Partial<
        TaskLogData & { description: string; assignee_id: number }
      >
    ) => {
      return communicationService.updateTask(Number(payload.id), payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useDeleteTaskMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (taskId: number) => {
      return communicationService.deleteTask(taskId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useUpdateCallNoteMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (
      payload: CallNotePayload & { callSessionId: CallLogData["id"] }
    ): Promise<CallLogData> =>
      communicationService.updateCallNote(payload.callSessionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useUpdateNoteMutation = (invalidateAll = false) => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  const { getNotableId } = useNotesStore();
  return useMutation({
    mutationFn: (payload: NotePayload): Promise<Note | void> => {
      const currentPayload: NotePayload = {
        ...payload,
        notable_id: getNotableId(payload.notable_id),
      };
      if (!payload.id) {
        showMessage(t("COMMON.ERROR_OCCURRED"), "error");
        return new Promise((resolve) => resolve());
      }
      return notesService.updateNote(currentPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (invalidateAll) {
            return queryKey[0] === COMMUNICATION_LOGS_KEY;
          }
          return (
            queryKey[0] === COMMUNICATION_LOGS_KEY &&
            (queryKey[1] as Record<string, string>)?.type === "note"
          );
        },
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useDeleteNoteMutation = (invalidateAll = false) => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: Note["id"]) => notesService.deleteNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (invalidateAll) {
            return queryKey[0] === COMMUNICATION_LOGS_KEY;
          }
          return (
            queryKey[0] === COMMUNICATION_LOGS_KEY &&
            (queryKey[1] as Record<string, string>)?.type === "note"
          );
        },
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useUpdateEmailModalDataMutation = () => {
  const store = useEmailsStore();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  const { modalData } = storeToRefs(store);

  return useMutation({
    mutationFn: async (payload: {
      application_ids?: string[];
      send_as_bulk?: boolean;
      email_thread_ids?: number[];
    }): Promise<void> => {
      if (!payload.application_ids && !payload.email_thread_ids) {
        return;
      }
      modalData.value =
        await emailService.getApplicationEmailModalData(payload);
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useSendEmailMutation = (invalidateAll = false) => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: async (payload: {
      email: EmailFormPayload;
      files: File[];
    }): Promise<void> => {
      await emailService.sendApplicationEmail(payload.email, payload.files);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (invalidateAll) {
            return (
              queryKey[0] === COMMUNICATION_LOGS_KEY ||
              queryKey[0] === EMAIL_THREAD_KEY
            );
          }
          return (
            (queryKey[0] === COMMUNICATION_LOGS_KEY &&
              (queryKey[1] as Record<string, string>)?.type === "email") ||
            queryKey[0] === EMAIL_THREAD_KEY
          );
        },
      });
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};

export const useEmailThreadQuery = (id: Ref<number>) => {
  const queryKey = computed(() => QUERY_KEYS.emailThread(id));

  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: async (): Promise<IPaginatedResponse<EmailThreadItem>> => {
      const response = await emailService.getEmailThread(id.value);
      return response;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    enabled: computed(() => !!unref(id)),
  });
};
